import { keys } from 'ramda';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import OverflowScrollWrapper from '~/components/molecule/OverflowScrollWrapper';
import useCurrentBreakpoint from '~/hooks/useCurrentBreakpoint';
import TEST_ID from './index.testid';

import KenmerkGroup from '../KenmerkGroup';
import type { RealworksRelatieSyncType } from '~/graphql/types';
import type {
  ActiveKenmerkSearch,
  Kenmerk,
  KenmerkLookup,
} from '../Synchronization/types';
import getKenmerkElementId from '~/components/page/Apps/Realworks/util/getKenmerkElementId';
import scrollElementIntoView from '~/util/scrollElementIntoView';

export type Props = {
  kenmerkLookup: KenmerkLookup;
  selectable?: boolean;
  syncType: RealworksRelatieSyncType;
  onChange: (args: { groupName: string; kenmerkList: Array<Kenmerk> }) => void;
  openGroupName: string | null;
  onOpenGroupChange: (groupName: string | null) => void;
  activeKenmerkSearch?: ActiveKenmerkSearch | null;
};

const RealworksSelectableAccordion: React.FCC<Props> = ({
  dataTestId,
  kenmerkLookup,
  onChange,
  syncType,
  selectable = true,
  openGroupName,
  onOpenGroupChange,
  activeKenmerkSearch = null,
  ...rest
}) => {
  const groupNames = keys(kenmerkLookup) as Array<string>;
  const currentBp = useCurrentBreakpoint();

  const handleToggle = (name: string) => {
    onOpenGroupChange(openGroupName === name ? null : name);
  };

  useEffect(() => {
    if (!activeKenmerkSearch || !openGroupName) return;

    void scrollElementIntoView(
      getKenmerkElementId(activeKenmerkSearch.kenmerkId),
      {
        initialDelay: 100,
      },
    );
  }, [activeKenmerkSearch, openGroupName]);

  return (
    <Container data-testid={dataTestId} {...rest}>
      <OverflowScrollWrapper
        style={{ maxHeight: currentBp === 'mobile' ? '100vh' : '50vh' }}
      >
        {groupNames.map((name: string) => (
          <KenmerkGroup
            key={`key-${name}`}
            kenmerken={kenmerkLookup[name]}
            name={name}
            onChange={({ kenmerkList }) =>
              onChange({ groupName: name, kenmerkList })
            }
            dataTestId={TEST_ID.KENMERK_GROUP}
            selectable={selectable}
            syncType={syncType}
            isOpen={openGroupName === name}
            onToggle={() => handleToggle(name)}
            activeKenmerkSearch={activeKenmerkSearch}
          />
        ))}
      </OverflowScrollWrapper>
    </Container>
  );
};

const Container = styled.div<{}>(
  ({ theme }) => css`
    width: 100%;
    ${theme.mq.lessThan('mobile')`
      margin: ${theme.space('l')} 0;
    `}
  `,
);

export default RealworksSelectableAccordion;
